if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.cleanupGiftWrapEasifyArtifacts();

        if (preventFocus) this.openedBy = null;
        super.hide();

        // Wait for the close transition to finish before actually clearing the
        // content. Clearing it immediately (before any CSS fade-out has had a
        // chance to play) makes it look like the box instantly goes blank
        // rather than the real content smoothly fading away with it.
        window.setTimeout(() => {
          this.modalContent.innerHTML = '';
        }, 300);
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');
        this.cleanupGiftWrapEasifyArtifacts();

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            this.reinitializeGiftWrapEasify(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      getGiftWrapProductId() {
        const formProductId = this.modalContent.querySelector('input[name="product-id"]')?.value;
        if (/^\d+$/.test(formProductId || '')) return formProductId;

        const modalProductId = this.id.replace('GiftWrapQuickAdd-', '');
        return /^\d+$/.test(modalProductId) ? modalProductId : null;
      }

      getGiftWrapProductHandle(productUrl) {
        if (!productUrl) return null;

        return productUrl
          .replace(window.location.origin, '')
          .split('?')[0]
          .replace(/^\/products\//, '')
          .replace(/\/$/, '');
      }

      ensureGiftWrapEasifyMount(productInfo) {
        const buttons = productInfo?.querySelector('.product-form__buttons');
        if (!buttons || !buttons.parentElement) return;

        let mount = productInfo.querySelector('.easify-product-options');
        if (!mount) {
          mount = document.createElement('div');
          mount.className = 'easify-product-options';
        }

        if (mount.nextElementSibling === buttons) return;
        buttons.parentElement.insertBefore(mount, buttons);
      }

      cleanupGiftWrapEasifyArtifacts() {
        if (!this.id.startsWith('GiftWrapQuickAdd-')) return;

        const staleSelectors = [
          '.tpo_option-set-wrapper',
          '.tpo_modal-tired-wholesale-wraper',
          '.tpo_total-additional-price-wrapper',
          '.tpo_total-additional-price-input',
        ];

        this.querySelectorAll(staleSelectors.join(',')).forEach((node) => node.remove());
      }

      async reinitializeGiftWrapEasify(opener) {
        if (!this.id.startsWith('GiftWrapQuickAdd-')) return;

        const productInfo = this.modalContent.querySelector('product-info');
        const productHandle = this.getGiftWrapProductHandle(opener?.getAttribute('data-product-url'));
        if (!productInfo) return;
        if (!productHandle) return;
        if (typeof window.easifyShowOptionSetOnCustomElement !== 'function') return;

        await this.mountGiftWrapEasify(productInfo, productHandle);
        this.hookGiftWrapEasifyRemount(productInfo, productHandle);
      }

      async mountGiftWrapEasify(productInfo, productHandle) {
        const productId = this.getGiftWrapProductId();
        if (!productInfo || !productId || !productHandle) return;

        this.cleanupGiftWrapEasifyArtifacts();
        this.ensureGiftWrapEasifyMount(productInfo);

        // Give the injected product form a frame to connect before handing it to Easify.
        await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));

        try {
          await window.easifyShowOptionSetOnCustomElement(productInfo, productId, productHandle);
        } catch (error) {
          console.error('Failed to initialize Easify Options inside gift-wrap quick add.', error);
        }
      }

      // Dawn swaps out the entire <product-info> node (rather than updating it
      // in place) whenever the customer changes a variant/option, via
      // HTMLUpdateUtility.viewTransition in product-info.js. That leaves
      // Easify's mounted widget - and any internal listeners/references it
      // holds onto that now-removed node - orphaned, which both makes the
      // widget vanish after a variant change and is the likely source of the
      // "querySelector of undefined" errors Easify throws afterward. Hook
      // product-info's own postProcessHtmlCallbacks (the same extensibility
      // point Dawn itself uses to reinit the payment button / 3D model viewer
      // after a swap) to re-mount Easify against the freshly-swapped node,
      // and re-arm the same hook on that new node so it survives repeated
      // variant changes.
      hookGiftWrapEasifyRemount(productInfo, productHandle) {
        if (productInfo.dataset.giftWrapEasifyHooked) return;
        productInfo.dataset.giftWrapEasifyHooked = 'true';

        productInfo.postProcessHtmlCallbacks.push((newProductInfo) => {
          this.mountGiftWrapEasify(newProductInfo, productHandle);
          this.hookGiftWrapEasifyRemount(newProductInfo, productHandle);
        });
      }
    }
  );
}
